import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { validateProfileField } from "@/frontend/utils";

export type ProfileFormData = {
	email: string;
	displayname: string;
	firstname: string;
	lastname: string;
	mobile?: string;
};

type Props = {
	open: boolean;
	onClose: () => void;
	title: string;
	user?:
		| {
				email: string;
				displayname?: string | null;
				firstname?: string | null;
				lastname?: string | null;
				mobile?: string | null;
		  }
		| null;
	onSubmit: (data: ProfileFormData) => Promise<void>;
	submitLabel?: string;
	isCreating?: boolean;
};

export default function ProfileDialog({
	open,
	onClose,
	title,
	user,
	onSubmit,
	submitLabel = "Speichern",
	isCreating = false,
}: Props) {
	const [form, setForm] = useState({
		email: "",
		displayname: "",
		firstname: "",
		lastname: "",
		mobile: "",
	});
	const [errors, setErrors] = useState<{ [k: string]: string | null }>({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Initialize when opened or when user changes
	useEffect(() => {
		if (!open) return;
		if (user) {
			setForm({
				email: user.email ?? "",
				displayname: user.displayname ?? "",
				firstname: user.firstname ?? "",
				lastname: user.lastname ?? "",
				mobile: user.mobile ?? "",
			});
		} else {
			// For creating new users, start with empty form
			setForm({
				email: "",
				displayname: "",
				firstname: "",
				lastname: "",
				mobile: "",
			});
		}
		setErrors({});
	}, [open, user]);

	const handleChange =
		(key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
			setForm((f) => ({ ...f, [key]: e.target.value }));
			setErrors((err) => ({ ...err, [key]: null }));
		};

	const validate = () => {
		const next: typeof errors = {};
		
		// Email validation (mandatory when creating)
		if (isCreating && !form.email.trim()) {
			next.email = "E-Mail ist erforderlich";
		}

		// Display name validation (mandatory)
		const displaynameErr = validateProfileField(form.displayname, "Anzeigename");
		if (displaynameErr) next.displayname = displaynameErr;

		// First name validation (only validate if not empty)
		if (form.firstname.trim()) {
			const firstnameErr = validateProfileField(form.firstname, "Vorname");
			if (firstnameErr) next.firstname = firstnameErr;
		}

		// Last name validation (only validate if not empty)
		if (form.lastname.trim()) {
			const lastnameErr = validateProfileField(form.lastname, "Nachname");
			if (lastnameErr) next.lastname = lastnameErr;
		}

		setErrors(next);
		return Object.values(next).every((v) => !v);
	};

	const handleSave = async () => {
		if (!validate()) return;
		setIsSubmitting(true);
		try {
			await onSubmit({
				email: form.email.trim(),
				displayname: form.displayname.trim(),
				firstname: form.firstname.trim(),
				lastname: form.lastname.trim(),
				mobile: form.mobile.trim() || undefined,
			});
		} catch (e) {
			// Map error messages if they reference fields
			const msg = (e instanceof Error ? e.message : typeof e === "string" ? e : String(e ?? "Fehler beim Speichern")) as string;
			if (msg) {
				const lower = msg.toLowerCase();
				const copy = { ...errors };
				if (lower.includes("anzeige") || lower.includes("display"))
					copy.displayname = msg;
				if (lower.includes("email") || lower.includes("mail"))
					copy.email = msg;
				if (lower.includes("vorname") || lower.includes("first"))
					copy.firstname = msg;
				if (lower.includes("nachname") || lower.includes("last"))
					copy.lastname = msg;
				setErrors(copy);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						label="E-Mail"
						value={form.email}
						onChange={isCreating ? handleChange("email") : undefined}
						InputProps={{ readOnly: !isCreating }}
						error={!!errors.email}
						helperText={errors.email || ""}
						fullWidth
						autoFocus={isCreating}
						required={isCreating}
					/>
					<TextField
						label="Anzeigename"
						value={form.displayname}
						onChange={handleChange("displayname")}
						error={!!errors.displayname}
						helperText={errors.displayname || ""}
						fullWidth
						autoFocus={!isCreating}
						required
					/>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<TextField
							label="Vorname"
							value={form.firstname}
							onChange={handleChange("firstname")}
							error={!!errors.firstname}
							helperText={errors.firstname || ""}
							fullWidth
						/>
						<TextField
							label="Nachname"
							value={form.lastname}
							onChange={handleChange("lastname")}
							error={!!errors.lastname}
							helperText={errors.lastname || ""}
							fullWidth
						/>
					</Stack>
					<TextField
						label="Mobilnummer"
						value={form.mobile}
						onChange={handleChange("mobile")}
						fullWidth
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isSubmitting}>
					Abbrechen
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={isSubmitting}
					startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
				>
					{submitLabel}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
